import { Component, Inject, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { from } from 'rxjs';
import { finalize, switchMap } from 'rxjs/operators';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { LoginService } from 'src/app/mobile/login/login.service';
import { BasicService } from 'src/app/shared/services/basic.service';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { limitationsDictionnary } from './shared/limitations-dictionnary.const';
import { PasswordChangeService } from './shared/password-change.service';
import { PasswordValidator } from './shared/password.validator';

@Component({
  selector: 'app-user-preferences-password-change',
  templateUrl: './user-preferences-password-change.component.html',
  styleUrls: [
    '../../../user-preferences/shared/user-preferences.scss',
    './user-preferences-password-change.component.scss',
  ],
  encapsulation: ViewEncapsulation.None,
})
export class UserPreferencesPasswordChangeComponent implements OnInit {
  @Input() loggedUser: any;
  @Input() confirm: any;

  needsOldPassword: boolean;
  form: FormGroup;
  introduction: string;
  limitations: { text: string; key: string; code: string }[] = [];
  limitationsDictionnary: { text: string; key: string; code: string }[] = limitationsDictionnary;
  showPassword = {
    password: false,
    'old-password': false,
    'confirm-password': false,
  };
  get passwordCtrl(): FormControl {
    return this.form?.get('password') as FormControl;
  }

  get oldPasswordCtrl(): FormControl {
    return this.form?.get('oldPassword') as FormControl;
  }

  get confirmPasswordCtrl(): FormControl {
    return this.form?.get('confirmPassword') as FormControl;
  }

  constructor(
    private formBuilder: FormBuilder,
    private alcmeonContextService: AlcmeonContextService,
    private basicService: BasicService,
    private dialog: MatDialog,
    private loginService: LoginService,
    private passwordChangeService: PasswordChangeService,
    @Inject('LoginFactory') private ajsLoginService: any,
    @Inject('PasswordConfirmFactory') private passwordConfirmService: any,
    @Inject('PreferencesFactory') private preferencesService: any,
    @Inject('TopToolbarFactory') private topToolbarService: any,
    @Inject('$state') private $state: any,
    @Inject('$stateParams') private $stateParams: any
  ) {}

  ngOnInit() {
    this.form = this.formBuilder.group({
      password: ['', Validators.required],
      confirmPassword: ['', Validators.required],
    });
    if (this.loggedUser) {
      this.needsOldPassword = true;
      this.topToolbarService.setTitleTranslation('preferences/password', null);
      this.form.addControl('oldPassword', new FormControl('', Validators.required));

      this.oldPasswordCtrl.valueChanges.subscribe(() => {
        if (this.oldPasswordCtrl.invalid && !this.passwordCtrl.value) {
          this.passwordCtrl.disable();
        } else {
          this.passwordCtrl.enable();
        }
      });
      this.oldPasswordCtrl.updateValueAndValidity();
    }

    this.passwordCtrl.valueChanges.subscribe(() => {
      if ((this.passwordCtrl.disabled || this.passwordCtrl.invalid) && !this.confirmPasswordCtrl.value) {
        this.confirmPasswordCtrl.disable();
      } else {
        this.confirmPasswordCtrl.enable();
        this.confirmPasswordCtrl.addValidators(PasswordValidator.validConfirmPassword(this.passwordCtrl));
      }
    });
    this.extractCompanyStrategy();
    this.passwordCtrl.updateValueAndValidity();
  }

  private extractCompanyStrategy() {
    const company = this.confirm || this.loggedUser?.companies[0];

    const { limitations, introduction } = this.passwordChangeService.extractCompanyStrategy(
      (this.confirm || this.loggedUser)?.email,
      company,
      this.passwordCtrl,
      this.loggedUser ? this.oldPasswordCtrl : null
    );

    this.limitations = limitations;
    this.introduction = introduction;
  }

  changePassword() {
    if (this.confirm) {
      this.resetPassword();
    } else if (this.needsOldPassword) {
      this.updatePassword();
    }
  }

  private updatePassword() {
    const { oldPassword, password, confirmPassword } = this.form.value;
    from(
      this.preferencesService.updatePassword(this.$stateParams.companyId, {
        old_password: oldPassword,
        password: password,
        confirm_password: confirmPassword,
      })
    )
      .pipe(finalize(() => this.form.reset()))
      .subscribe({
        next: (result: any) => {
          try {
            if (typeof result.status !== 'undefined' && typeof result.data.error === 'string') {
              const translation = this.basicService.translate(result.data.error || 'password-confirm/error');
              const message = translation.replace(
                'FORGOT_URL',
                `${this.basicService.fullURLForRoutePath('password-request')}?email=${this.loggedUser.email}`
              );
              this.basicService.toastAlert(message);
            } else if (result.data?.error && typeof result.data.error !== 'string') {
              const translation = this.basicService.translate('password-confirm/error');
              const message = translation.replace(
                'FORGOT_URL',
                `${this.basicService.fullURLForRoutePath('password-request')}?email=${this.loggedUser.email}`
              );
              this.basicService.toastAlert(message);
            } else {
              this.basicService.toastSuccess(result.message);
            }
          } catch {
            this.basicService.toastAlert('password-confirm/error');
          }
        },
      });
  }

  private resetPassword() {
    const { password } = this.form.value;
    const { confirmationId, confirmationUid, companyName } = this.$stateParams;
    this.loginService
      .convertCompanyCode(companyName)
      .pipe(
        switchMap(() =>
          from(
            this.passwordConfirmService.set(
              this.alcmeonContextService.companyId,
              confirmationId,
              confirmationUid,
              password
            )
          )
        )
      )
      .subscribe({
        next: () => {
          const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
              title: this.basicService.translate('password-confirm/title'),
              description: this.basicService.translate('password-confirm/ok'),
              confirmBtnText: this.basicService.translate('password-confirm/goto-login'),
            },
          });
          dialogRef.afterClosed().subscribe(() => {
            from(
              this.ajsLoginService.doLogin(
                this.alcmeonContextService.companyId,
                this.confirm.email,
                password,
                null,
                null
              )
            ).subscribe({
              error: () => {
                // we can't login directly. Redirects to login page.
                this.$state.go('^.login');
              },
            });
          });
        },
        error: response => {
          if (response.data?.error?.reason === 'new-password-same-as-old') {
            this.basicService.toastAlert('password-confirm/dont-reuse-password');
          } else if (response.data?.error?.reason) {
            this.basicService.toastAlert(response.data?.error?.reason || 'password-confirm/error');
          } else {
            // The only error that should happen here is confirmation-expired
            this.$state.go('^.password-confirm-expired');
          }
        },
      });
  }
}
